import { Button, Grid, Stack, Typography } from '@mui/material'
import { EventDto } from '../../interfaces/IEvent'
import { EventType } from '../../interfaces/IEventEnums'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useContext, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'
import EventTypeSelectionCards from './EventTypeSelectionCards'
import CreateGenericEventGrid from './EventGrids/CreateGenericEventGrid'
import CreateIncidentEventGrid from './EventGrids/CreateIncidentEventGrid'
import CreatePlannedWorkEventGrid from './EventGrids/CreatePlannedWorkEventGrid'
import { CustomContainer } from '../../components/Wrapper/CustomContainer'
import ResponsiveDivider from '../../components/Divider/ResponsiveDivider'
import RootBreadcrumb from '../../components/navigation/RootBreadcrumb'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'

function CreateEvent() {
  const { context } = useContext(EbAuthenticatedAppContext)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fromDate = searchParams.get('fromDate') ? new Date(searchParams.get('fromDate') as string) : new Date()
  const [newEvent, setNewEvent] = useState({ type: searchParams.get('eventType'), eventDetails: { fromDate: fromDate } } as EventDto)
  const [sendLoading, setSendLoading] = useState(false)

  const handleEventTypeChange = (name: string, eventType: EventType | null) => {
    setNewEvent((prevState: any) => ({
      ...prevState,
      [name]: eventType,
    }))
  }

  const handleEventUpdate = (updatedEvent: EventDto) => {
    setNewEvent(updatedEvent)
  }

  const handleAbortCreateEvent = () => {
    navigate(`/`)
  }

  const handleCreateEvent = async (event: any) => {
    setSendLoading(true)

    event.preventDefault()

    var response = await context.restApiClient.buildRequest('/api/create-event').serializeToJsonContent(newEvent).postAsync()

    if (response.response?.content) {
      const eventId = response.response?.content

      enqueueSnackbar(<Typography variant="body2">{eventId} opprettet.</Typography>, { variant: 'success' })
      navigate(`/events/${eventId}`)

      return setSendLoading(false)
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke opprette hendelse. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
        variant: 'error',
      })

      return setSendLoading(false)
    }

    enqueueSnackbar(<Typography variant="body2">Kunne ikke opprette hendelse. Prøv igjen eller kontakt systemansvarlig.</Typography>, {
      variant: 'error',
    })

    setSendLoading(false)
  }

  return (
    <form onSubmit={handleCreateEvent}>
      <CustomContainer maxWidth={'xl'}>
        <RootBreadcrumb>
          <Stack direction="row">
            <Typography>{'Ny hendelse'}</Typography>
          </Stack>
        </RootBreadcrumb>

        <Typography variant="h5" sx={{
          marginTop: 4
        }}>
          Ny hendelse
        </Typography>

        <ResponsiveDivider />

        <Grid
          container
          rowSpacing={4}
          columnSpacing={3}
          sx={{
            flexDirection: 'row',
            justifyContent: 'center'
          }}>
          <EventTypeSelectionCards newEvent={newEvent} handleEventTypeChange={handleEventTypeChange} />

          {newEvent.type === EventType.Generell && <CreateGenericEventGrid onUpdate={handleEventUpdate} preDefinedFromDate={fromDate} />}
          {newEvent.type === EventType.Fellesfeil && <CreateIncidentEventGrid onUpdate={handleEventUpdate} preDefinedFromDate={fromDate} />}
          {newEvent.type === EventType.Planlagt && <CreatePlannedWorkEventGrid onUpdate={handleEventUpdate} preDefinedFromDate={fromDate} />}

          {newEvent.type && (
            <>
              <Grid
                size={6}
                sx={{
                  marginLeft: 0,
                  justifyContent: 'right'
                }}>
                <Button variant="contained" color="error" size="large" onClick={handleAbortCreateEvent} disabled={sendLoading}>
                  Avbryt
                </Button>
              </Grid>
              <Grid
                size={6}
                sx={{
                  marginLeft: 0,
                  textAlign: 'right'
                }}>
                <Button
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={sendLoading}
                  startIcon={sendLoading ? <LoadingIndicator size={'xsmall'} /> : null}
                >
                  Lagre
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </CustomContainer>
    </form>
  );
}

export default CreateEvent
